import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Mic, ClipboardList, Users, Package, ChevronRight, Video } from "lucide-react";
import { SeverityBadge } from "@/components/SeverityBadge";
import { SessionRecapCard } from "@/components/SessionRecapCard";
import { FeaturedTherapyCard } from "@/components/FeaturedTherapyCard";
import { CarePlanCard } from "@/components/CarePlanCard";
import { StreakBadge } from "@/components/StreakBadge";
import { useI18n } from "@/i18n";

export default function Home() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [reports, setReports] = useState([]);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    api.get("/reports").then((r) => setReports(r.data)).catch(() => {});
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  }, []);

  const lastReport = reports[0];

  return (
    <div className="space-y-8" data-testid="home-page">
      <section className="rounded-3xl bg-[color:var(--mano-hero)] text-white p-8 lg:p-10 relative overflow-hidden grain">
        <span className="uppercase-label text-white/70">{t("home.greeting")}, {user?.name?.split(" ")[0]}</span>
        <h1 className="font-display text-3xl lg:text-4xl mt-3 leading-tight">
          {t("home.hero_title")}
        </h1>
        <p className="text-white/80 mt-3 max-w-lg">
          {t("home.hero_sub")}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/screening"
            data-testid="home-start-voice-btn"
            className="inline-flex items-center gap-2 bg-white text-[color:var(--mano-hero)] px-5 py-3 rounded-full font-semibold hover:bg-[color:var(--mano-mild)] transition-colors"
          >
            <Mic size={16} /> {t("home.start_voice")}
          </Link>
          <Link
            to="/voicelab"
            data-testid="home-open-voicelab-btn"
            className="inline-flex items-center gap-2 border border-white/30 text-white px-5 py-3 rounded-full font-semibold hover:bg-white/10 transition-colors"
          >
            <ClipboardList size={16} /> {t("home.open_voicelab")}
          </Link>
        </div>
      </section>

      {lastReport && (
        <section className="rounded-2xl bg-white border border-[color:var(--mano-border)] p-6" data-testid="home-last-report">
          <div className="flex items-center justify-between">
            <div>
              <div className="uppercase-label">{t("home.latest_report")}</div>
              <div className="font-display text-xl mt-1">
                {lastReport.kind === "voice"
                  ? `Voice · ${lastReport.category.replace("_", " ")}`
                  : lastReport.questionnaire_name}
              </div>
            </div>
            <SeverityBadge band={lastReport.band} size="lg" />
          </div>
          <Link
            to={`/report/${lastReport.id}`}
            data-testid="home-view-last-report"
            className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-[color:var(--mano-primary)]"
          >
            {t("home.view_report")} <ChevronRight size={14} />
          </Link>
        </section>
      )}

      <SessionRecapCard />

      <StreakBadge />

      <CarePlanCard />

      <FeaturedTherapyCard />

      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <QuickCard to="/assess" icon={Mic} title={t("quick.assess")} body={t("quick.assess_sub")} testid="quick-assess" />
        <QuickCard to="/voicelab" icon={ClipboardList} title={t("quick.voicelab")} body={t("quick.voicelab_sub")} testid="quick-voicelab" />
        <QuickCard to="/experts" icon={Users} title={t("quick.experts")} body={t("quick.experts_sub")} testid="quick-experts" />
        <QuickCard to="/therapies" icon={Package} title={t("quick.therapies")} body={t("quick.therapies_sub")} testid="quick-therapies" />
      </section>

      {bookings.length > 0 && (
        <section className="rounded-2xl bg-white border border-[color:var(--mano-border)] p-6" data-testid="home-upcoming-bookings">
          <div className="uppercase-label">Upcoming sessions</div>
          <div className="mt-4 space-y-3">
            {bookings.slice(0, 3).map((b) => (
              <div key={b.id} className="flex items-center justify-between border border-[color:var(--mano-border)] rounded-xl p-4">
                <div>
                  <div className="font-semibold">{b.expert_name}</div>
                  <div className="text-sm text-[color:var(--mano-ink-soft)]">
                    {new Date(b.start_iso).toLocaleString()}
                  </div>
                </div>
                <Link
                  to={`/session/${b.id}`}
                  data-testid={`join-meeting-${b.id}`}
                  className="text-sm font-semibold text-[color:var(--mano-primary)] inline-flex items-center gap-1"
                >
                  <Video size={14} /> Join
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function QuickCard({ to, icon: Icon, title, body, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group rounded-2xl bg-white border border-[color:var(--mano-border)] p-5 hover:border-[color:var(--mano-primary)] transition-colors"
    >
      <div className="h-10 w-10 rounded-xl bg-[color:var(--mano-border)]/50 flex items-center justify-center text-[color:var(--mano-primary)] group-hover:bg-[color:var(--mano-primary)] group-hover:text-white transition-colors">
        <Icon size={18} />
      </div>
      <div className="font-semibold mt-3">{title}</div>
      <div className="text-sm text-[color:var(--mano-ink-soft)]">{body}</div>
    </Link>
  );
}
